import { useNotificationStore } from '~/stores/notification.store'

export interface NotificationAction {
  action: string
  title: string
  icon?: string
}

export interface NotificationOptions {
  title: string
  body: string
  icon?: string
  badge?: string
  tag?: string
  data?: any
  actions?: NotificationAction[]
  requireInteraction?: boolean
  silent?: boolean
}

export const useNotifications = () => {
  const notificationStore = useNotificationStore()
  const user = useUserStore()
  const vapidPublicKey = ref(
    'BOysiemR2WI9Wmy4_F0SbKXxAorFC0Ga90I5jmiybhHmLdFztNKlYLsnD7gNGRpZIh0HAbPvxl3ezAE_0TW_jB0'
  )

  const webPushEndpoint = computed(() => (user.isLoggedInAsContact ? 'css/web-push' : 'web-push'))

  // Initialize notifications when composable is used
  onMounted(async () => {
    // Wait for service worker to be ready before setting up listeners
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      try {
        await navigator.serviceWorker.ready
        navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage)
      } catch (error) {
        navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage)
      }
    }
  })

  onUnmounted(() => {
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage)
    }
  })

  const handleServiceWorkerMessage = (event: MessageEvent) => {
    const { type, data, action } = event.data

    switch (type) {
      case 'NOTIFICATION_CLICK':
        handleNotificationClick(action, data)
        break
      case 'NOTIFICATION_ACTION':
        handleNotificationAction(action, data)
        break
    }
  }

  const handleNotificationClick = (data: any, tag?: string) => {
    // Mark notification as read if it exists in store
    if (tag) {
      const notification = notificationStore.notifications.find((n) => n.tag === tag)
      if (notification) {
        notificationStore.markAsRead(notification.id)
      }
    }

    // Handle navigation or other actions based on notification data
    if (data?.url) {
      navigateTo(data.url)
    }
  }

  const handleNotificationAction = (action: string, data: any) => {
    // Handle different notification actions
    switch (action) {
      case 'view':
        if (data?.url) {
          navigateTo(data.url)
        }
        break
      case 'dismiss':
        // Already handled by closing notification
        break
      // Add more actions as needed
    }
  }

  const requestPermission = async () => {
    return await notificationStore.requestPermission()
  }

  const subscribe = async () => {
    const subscription = await notificationStore.subscribe(vapidPublicKey.value)

    if (subscription) {
      sendSubscriptionToBackend(subscription)
    }

    return subscription
  }

  const unsubscribe = async () => {
    try {
      // Get subscription data BEFORE unsubscribing
      const currentSubscription =
        notificationStore.subscription || (await notificationStore.getExistingSubscription())

      const success = await notificationStore.unsubscribe()

      if (success && currentSubscription) {
        // Notify backend about unsubscription
        await removeSubscriptionFromBackend(currentSubscription)
      }

      return success
    } catch {}
  }

  const sendSubscriptionToBackend = (subscription: PushSubscription) => {
    const subscriptionData = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: subscription.getKey('p256dh')
          ? btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!)))
          : null,
        auth: subscription.getKey('auth')
          ? btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!)))
          : null
      }
    }

    // Use your existing API structure
    useApi(`${webPushEndpoint.value}/subscribe`, 'POST', subscriptionData)
  }

  const removeSubscriptionFromBackend = (subscription?: PushSubscription) => {
    try {
      if (subscription) {
        const subscriptionData = {
          endpoint: subscription.endpoint
        }

        // Use your existing API structure
        useApi(`${webPushEndpoint.value}/unsubscribe`, 'POST', subscriptionData)
      } else {
        // Still call the endpoint even without subscription data
        useApi(`${webPushEndpoint.value}/unsubscribe`, 'POST')
      }
    } catch {}
  }

  const showLocalNotification = (options: NotificationOptions) => {
    if (!notificationStore.canSendNotifications) {
      return
    }

    const notification = new Notification(options.title, {
      body: options.body,
      icon: options.icon || '/icons/Pflegering_Logo_192x192.png',
      badge: options.badge || '/icons/Pflegering_Logo_64x64.png',
      tag: options.tag,
      data: options.data,
      requireInteraction: options.requireInteraction,
      silent: options.silent
    })

    // Add to store for tracking
    notificationStore.addNotification({
      title: options.title,
      body: options.body,
      icon: options.icon,
      badge: options.badge,
      tag: options.tag,
      data: options.data
    })

    // Handle click events
    notification.onclick = () => {
      handleNotificationClick(options.data, options.tag)
      notification.close()
    }

    return notification
  }

  const checkExistingSubscription = async (): Promise<PushSubscription | null> => {
    return await notificationStore.getExistingSubscription()
  }

  const getServerSubscriptions = async (): Promise<any[]> => {
    return await notificationStore.getServerSubscriptions()
  }

  return {
    // Store getters
    permission: computed(() => notificationStore.permission),
    isSupported: computed(() => notificationStore.isSupported),
    canSendNotifications: computed(() => notificationStore.canSendNotifications),
    isSubscribed: computed(() => notificationStore.isSubscribed),
    notifications: computed(() => notificationStore.notifications),
    unreadCount: computed(() => notificationStore.unreadCount),

    // Actions
    requestPermission,
    subscribe,
    unsubscribe,
    showLocalNotification,
    checkExistingSubscription,
    getServerSubscriptions,

    // Store actions
    markAsRead: notificationStore.markAsRead,
    markAllAsRead: notificationStore.markAllAsRead,
    removeNotification: notificationStore.removeNotification,
    clearAllNotifications: notificationStore.clearAllNotifications
  }
}
